<template>
    <div class="flex h-screen bg-gray-50">
        <SideBar class="flex-shrink-0" />

        <div class="flex-1 overflow-auto p-4 md:p-6">
            <!-- Phone dialer container -->
        <div class="max-w-md w-full mx-auto flex-1 flex flex-col p-4 overflow-hidden h-full">
            <!-- Status bar -->
            <div
            v-if="isIncomingCall"
            class="mb-4 p-4 bg-white border border-gray-200 rounded-2xl shadow-md flex items-center justify-between"
        >
            <div>
            <p class="text-xs text-gray-500 uppercase">Incoming call</p>
            <p class="text-lg font-semibold text-gray-800">{{ incomingCallerNumber }}</p>
            </div>
            <div class="flex space-x-3">
            <button
                @click="rejectCall"
                class="p-3 bg-red-100 rounded-full hover:bg-red-200 transition"
            >
                <PhoneOff class="h-5 w-5 text-red-600" />
            </button>
            <button
                @click="acceptCall"
                class="p-3 bg-primary rounded-full hover:bg-emerald-200 transition"
            >
                <Phone class="h-5 w-5 bg-primary" />
            </button>
            </div>
        </div>
            <div class="mb-4 text-center">
            <div class="text-sm font-medium text-gray-500">
                {{ callStatus }}
            </div>
            <div v-if="isCallActive" class="text-sm font-medium text-emerald-600">
                {{ formatTime(callDuration) }}
            </div>
            </div>
    
            <!-- Spacer to push content to center -->
            <div class="flex-grow"></div>
    
            <!-- Phone number input -->
            <div class="relative mb-6">
            <input
                type="text"
                v-model="phoneNumber"
                class="w-full px-4 py-3 text-xl text-center font-medium rounded-lg bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter phone number"
                autofocus
            />
            <button
                v-if="phoneNumber"
                @click="phoneNumber = ''"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
                <X class="h-5 w-5" />
            </button>
            </div>
    
            <!-- Dialpad -->
            <div class="grid grid-cols-3 gap-4 mb-6">
            <button
                v-for="key in dialpadKeys"
                :key="key"
                @click="onDialpadPress(key)"
                class="aspect-square flex items-center justify-center text-2xl font-medium rounded-full bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
            >
                {{ key }}
            </button>
            </div>
    
            <!-- Call controls -->
            <div class="mb-6">
            <div class="flex justify-center space-x-4 mb-6">
                <!-- Mute button -->
                <button
                @click="toggleMute"
                class="p-7 rounded-full flex items-center justify-center"
                :class="isMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'"
                >
                <Mic v-if="!isMuted" class="h-6 w-6" />
                <MicOff v-else class="h-6 w-6" />
                </button>
    
                <!-- Call/Hangup button -->
                <button
                @click="isCallActive ? endCall() : startCall()"
                class="p-7 rounded-full flex items-center justify-center"
                :class="isCallActive ? 'bg-red-500 text-black' : 'bg-primary text-black'"
                >
                <Phone v-if="!isCallActive" class="h-8 w-8" />
                <PhoneOff v-else class="h-8 w-8" />
                </button>
    
                <!-- Settings button -->
                <button
                @click="toggleSettings"
                class="p-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center"
                :class="showSettings ? 'bg-emerald-100 text-emerald-600' : ''"
                >
                <Settings class="h-6 w-6" />
                </button>
            </div>
            </div>
    
            <!-- Spacer to push settings to bottom -->
            <div class="flex-grow"></div>
    
            <!-- Toast notifications -->
            <div class="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
            <div
                v-if="toast.show"
                class="px-4 py-2 rounded-lg shadow-lg text-black"
                :class="toast.type === 'error' ? 'bg-red-500' : 'bg-primary'"
            >
                {{ toast.message }}
            </div>
            </div>
    
            <!-- Audio elements (hidden) -->
            <audio ref="remoteAudio" autoplay></audio>
            <audio ref="localAudio" muted></audio>
            <audio
              ref="ringtone"
              :src="ringSrc"
              :volume=".07"
              loop
              preload="auto"
          ></audio>
        </div>
        </div>

        <div
    v-if="showSettings"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
  >
    <div class="bg-white rounded-2xl w-full max-w-md max-h-full overflow-y-auto p-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium">Audio Settings</h3>
        <button @click="toggleSettings" class="text-gray-500 hover:text-gray-700">
          ✕
        </button>
      </div>

      <!-- Microphone selector -->
      <div class="mb-4">
        <label for="input-device" class="block text-sm font-medium text-gray-700 mb-1">
          Microphone
        </label>
        <select
          id="input-device"
          v-model="selectedInputDevice"
          @change="changeInputDevice"
          class="w-full px-3 py-2 bg-gray-100 rounded-md focus:outline-none"
        >
          <option
            v-for="device in audioInputDevices"
            :key="device.id"
            :value="device.id"
          >
            {{ device.label }}
          </option>
        </select>
      </div>

      <!-- Speaker selector -->
      <div class="mb-4">
        <label for="output-device" class="block text-sm font-medium text-gray-700 mb-1">
          Speaker
        </label>
        <select
          id="output-device"
          v-model="selectedOutputDevice"
          @change="changeOutputDevice"
          class="w-full px-3 py-2 bg-gray-100 rounded-md focus:outline-none"
        >
          <option
            v-for="device in audioOutputDevices"
            :key="device.id"
            :value="device.id"
          >
            {{ device.label }}
          </option>
        </select>
      </div>
    </div>
    </div>
  </div>

  <!-- Call Review Prompt Modal -->
  <div
    v-if="showReviewModal"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 animate-in fade-in duration-300"
  >
    <div class="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl text-center transform transition-all scale-100">
      <div class="h-16 w-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      
      <h3 class="text-2xl font-bold text-gray-900 mb-2">Daily Review Pending</h3>
      <p class="text-gray-500 mb-8">
        You have analyzed calls waiting for your review. Completing these reviews helps improve your performance score.
      </p>

      <div class="space-y-3">
        <button 
          @click="router.push('/call-review')"
          class="w-full py-3.5 bg-primary text-black rounded-xl font-bold text-lg hover:bg-primary/90 transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          Review Calls Now
        </button>
        
        <button 
          @click="showReviewModal = false"
          class="text-sm text-gray-400 hover:text-gray-600 font-medium py-2 transition-colors"
        >
          Skip and go to Dialer
        </button>
      </div>
    </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onUnmounted,watch } from 'vue';
  import { Phone, PhoneOff, Mic, MicOff,X, Settings } from 'lucide-vue-next';
  import SideBar from '@/components/SideBar.vue';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import {TelnyxRTC} from '@telnyx/webrtc'
  import { rejectCall as apiRejectCall } from '@/api/dialer'
  import { getAnalyzedCalls, logTelnyxDebug, logCallDebug } from '@/api/calls'
  import ringSrc from '@/assets/ring.wav'
  import callingSound from '@/assets/calling_sound.mp3'

  const store = useStore();
  const router = useRouter();
  let client = null

  // State
  const phoneNumber = ref('');
  const callStatus = ref('Ready');
  const isCallActive = ref(false);
  const callDuration = ref(0);
  const isMuted = ref(false);
  const timerInterval = ref(null);
  const remoteAudio = ref(null);
  const localAudio = ref(null);
  const mediaStream = ref(null);
  const ringtone = ref(null);

  const toast = ref({
    show: false,
    message: '',
    type: 'info',
    timeout: null
  });
  
  const showSettings = ref(false);
  const showReviewModal = ref(false);
  const audioInputDevices = ref([]);
  const audioOutputDevices = ref([]);
  const selectedInputDevice = ref('');
  const selectedOutputDevice = ref('');
  const isReady = ref(false)  
  // Dialpad keys
  const dialpadKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
  const currentCall = ref(null)
  const isIncomingCall = ref(false);
  const incomingCallerNumber = ref('');

  let pstnHangupCalled = false;

  watch(isIncomingCall, (incoming) => {
  if (ringtone.value) {
    if (incoming) {
      ringtone.value.muted = false;
      ringtone.value.currentTime = 0;
      ringtone.value.play().catch(() => {});
    } else {
      ringtone.value.pause();
      ringtone.value.currentTime = 0;
      ringtone.value.muted = true;
    }
  }
});

  const handleKeydown = (e) => {
    const k = e.key;
    if (dialpadKeys.includes(k)) {
        appendDigit(k);
        e.preventDefault();
    } else if (k === 'Backspace') {
        phoneNumber.value = phoneNumber.value.slice(0, -1);
        e.preventDefault();
    }
  }       

  // Methods
  const appendDigit = (digit) => {
    phoneNumber.value += digit;
  };

  const onDialpadPress = (key) => {
    const call = currentCall.value;
    // If there is an active call, send DTMF; otherwise build the number
    if (call && call.state === 'active') {
      try {
        if (typeof call.dtmf === 'function') {
          call.dtmf(String(key)); // '0'-'9', '*', '#'
          appendDigit(key);
        } else {
          showToast('DTMF not supported on call', 'error');
        }
      } catch (e) {
        console.error('DTMF error:', e);
        showToast('DTMF failed', 'error');
      }
    } else {
      appendDigit(key);
    }
  };



  const doPstnHangup = async (ctrl, sessionId) => {
  if (pstnHangupCalled) return;
  pstnHangupCalled = true;
  await apiRejectCall(ctrl, sessionId);
};

  const startCall = async () => {
  pstnHangupCalled = false;
  isMuted.value = false;

  if (!phoneNumber.value) {
    return showToast('Please enter a phone number', 'error')
  }
  if (!client || !isReady.value) {
    return showToast('Dialer not ready', 'error')
  }

  const user = store.getters['auth/currentUser'];
  const myDID = user?.phone_number;
  console.log('[DEBUG] outbound DID:', myDID, 'store user:', user);

  if (!myDID) {
    return showToast('No outbound number configured', 'error');
  }

  callStatus.value = 'Calling…'
  const raw = phoneNumber.value;
  const cleaned = raw
    .replace(/[^\d+]/g, '')        // remove everything but digits and '+'
    .replace(/^(00)/, '+')         // convert leading 00→+
    .replace(/^(?!\+)/, '+1');     // default country code US if no '+'

  phoneNumber.value = cleaned;
  
  // Prepare the request payload for logging
  const requestPayload = {
    connection:        '2688723432173995322',
    callerNumber:  myDID,
    destinationNumber: phoneNumber.value,
    micId: selectedInputDevice.value, 
    speakerId: selectedOutputDevice.value
  };

  try {
    // Log the request going into newCall
    console.log('[DEBUG] client.newCall() request:', requestPayload);
    
    // get the Call instance
    const call = client.newCall(requestPayload)

    // Log the call object we get back
    console.log('[DEBUG] client.newCall() response:', call);
    
    // Log to debug API - success case
    await logTelnyxDebug({
      endpoint: 'client.newCall()',
      method: 'SDK_CALL',
      request_payload: requestPayload,
      response_data: {
        callId: call?.id,
        state: call?.state,
        direction: call?.direction,
        options: call?.options
      },
      status_code: 200,
      error_message: null
    }).catch(err => console.error('[DEBUG] Failed to log debug data:', err));

    // save it for later hangup
    currentCall.value = call

  } catch (err) {
    console.error('[DEBUG] Failed to start call:', err)
    
    // Log to debug API - error case
    await logTelnyxDebug({
      endpoint: 'client.newCall()',
      method: 'SDK_CALL',
      request_payload: requestPayload,
      response_data: null,
      status_code: 500,
      error_message: err?.message || JSON.stringify(err),
      error_name: err?.name,
      error_stack: err?.stack
    }).catch(logErr => console.error('[DEBUG] Failed to log error data:', logErr));
    
    showToast('Could not start call', 'error')
  }
}



const endCall = async (doWebrtc = true) => {
  // reset incoming UI
  isIncomingCall.value = false;
  incomingCallerNumber.value = null;
  isMuted.value = false;

  ringtone.value.pause();
  ringtone.value.currentTime = 0;

  const callObj = currentCall.value;
  if (!callObj) return;

  // 1) Hang up WebRTC leg if requested
  if (doWebrtc && typeof callObj.hangup === 'function') {
    callObj.hangup();
  }

  // 2) Hang up PSTN leg once for inbound calls
  const ctrl =
    callObj.options.telnyxCallControlId ||
    callObj.options.telnyx_call_control_id;
  const sessionId =
    callObj.options.telnyxSessionId ||
    callObj.options.telnyx_session_id;
  if (callObj.direction === 'inbound') {
    await doPstnHangup(ctrl, sessionId);
  }

  // 3) Reset UI/state
  currentCall.value = null;
  isCallActive.value = false;
  callStatus.value = 'Call ended';
  stopTimer();

  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(t => t.stop());
    mediaStream.value = null;
  }

  showToast('Call ended', 'info');

  // restore ready state after a moment
  setTimeout(() => {
    callStatus.value = 'Ready';
    callDuration.value = 0;
  }, 500);
};


const toggleMute = () => {
  const callObj = currentCall.value;

  if (!callObj || typeof callObj.toggleAudioMute !== 'function') {
    showToast('Call object missing or invalid', 'error');
    return;
  }

  console.log('🟡 Before toggle - muted state unknown, toggling anyway');

  try {
    callObj.toggleAudioMute();
    isMuted.value = !isMuted.value;
    console.log('🟢 toggleAudioMute called successfully');
    showToast(isMuted.value ? 'Mic muted' : 'Mic unmuted', 'info');
  } catch (err) {
    console.error('🔴 toggleAudioMute failed:', err);
    showToast('Toggle mute failed', 'error');
  }
};


  
  
  const startTimer = () => {
    stopTimer(); // Clear any existing timer
    timerInterval.value = setInterval(() => {
      callDuration.value += 1;
    }, 1000);
  };
  
  const stopTimer = () => {
    if (timerInterval.value) {
      clearInterval(timerInterval.value);
      timerInterval.value = null;
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const showToast = (message, type = 'info') => {
    // Clear existing timeout
    if (toast.value.timeout) {
      clearTimeout(toast.value.timeout);
    }
    
    // Show new toast
    toast.value = {
      show: true,
      message,
      type,
      timeout: setTimeout(() => {
        toast.value.show = false;
      }, 3000)
    };
  };
  
  const toggleSettings = () => {
    showSettings.value = !showSettings.value;
    if (showSettings.value) {
      enumerateDevices();
    }
  };
  
  const enumerateDevices = async () => {
    try {
      // Ensure we have permission to access devices
      if (!mediaStream.value) {
        const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        tempStream.getTracks().forEach(track => track.stop());
      }
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // Filter and map input (microphone) devices
      audioInputDevices.value = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Microphone ${audioInputDevices.value.length + 1}`
        }));
      
      // Filter and map output (speaker) devices
      audioOutputDevices.value = devices
        .filter(device => device.kind === 'audiooutput')
        .map(device => ({
          id: device.deviceId,
          label: device.label || `Speaker ${audioOutputDevices.value.length + 1}`
        }));
      
      // Set default selections if not already set
      if (!selectedInputDevice.value && audioInputDevices.value.length > 0) {
        selectedInputDevice.value = audioInputDevices.value[0].id;
      }
      
      if (!selectedOutputDevice.value && audioOutputDevices.value.length > 0) {
        selectedOutputDevice.value = audioOutputDevices.value[0].id;
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
      showToast('Could not access audio devices', 'error');
    }
  };
  
  const changeInputDevice = async () => {
  if (!selectedInputDevice.value) return;

  try {
    // Stop current tracks
    if (mediaStream.value) {
      mediaStream.value.getTracks().forEach(t => t.stop());
    }

    // Get new stream with selected device
    const constraints = {
      audio: { deviceId: { exact: selectedInputDevice.value } }
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    mediaStream.value = stream;

    if (localAudio.value) {
      localAudio.value.srcObject = stream;
    }

    // Swap mic on the actual call
    if (
      isCallActive.value &&
      currentCall.value &&
      typeof currentCall.value.setAudioInDevice === 'function'
    ) {
      await currentCall.value.setAudioInDevice(selectedInputDevice.value);
    }

    // Apply mute state to new track
    stream.getAudioTracks().forEach(track => {
      track.enabled = !isMuted.value;
    });

    showToast('Microphone changed', 'info');
  } catch (error) {
    console.error('Error changing input device:', error);
    showToast('Could not change microphone', 'error');
  }
};

  
const changeOutputDevice = async () => {
  if (!selectedOutputDevice.value || !remoteAudio.value || !client) return;

  try {
    // 1) Tell Telnyx to use the new speaker
    if (typeof client.setAudioOutputDevice === 'function') {
      await client.setAudioOutputDevice(selectedOutputDevice.value);
    }

    // 2) Force the <audio> element to that output
    if (typeof remoteAudio.value.setSinkId === 'function') {
      await remoteAudio.value.setSinkId(selectedOutputDevice.value);
    }

    showToast('Speaker changed', 'info');
  } catch (error) {
    console.error('Error changing speaker:', error);
    showToast('Could not change speaker', 'error');
  }
};

const acceptCall = () => {
  const callObj = currentCall.value;
  if (!callObj?.answer) return;

  // answer
  callObj.answer();

  // ensure SDK re‑routes audio into your <audio>
  if (typeof callObj.setAudioOutputDevice === 'function') {
    callObj.setAudioOutputDevice(selectedOutputDevice.value);
  }
  // fallback: manually attach WebRTC stream
  else if (callObj.mediaStream) {
    remoteAudio.value.srcObject = callObj.mediaStream;
    remoteAudio.value.play().catch(() => {});
  }else if (callObj.remoteStream) {
    remoteAudio.value.srcObject = callObj.remoteStream;
    remoteAudio.value.play().catch(() => {});
  }

  // update UI
  isIncomingCall.value = false;
  callStatus.value     = 'Connected';
  isCallActive.value   = true;
  startTimer();

  ringtone.value.pause();
  ringtone.value.currentTime = 0;

};

const rejectCall = async () => {
  const callObj = currentCall.value;
  if (!callObj) return;

  // 1) WebRTC reject
  if (typeof callObj.reject === 'function') {
  callObj.reject();
} else if (typeof callObj.hangup === 'function') {
  callObj.hangup();
}


  // 2) PSTN hangup once
  const ctrl = callObj.options.telnyxCallControlId || callObj.options.telnyx_call_control_id;
  const sessionId = callObj.options.telnyxSessionId || callObj.options.telnyx_session_id;
  await doPstnHangup(ctrl, sessionId);

  // 3) UI cleanup (skip PSTN hangup here)
  endCall(false);
};


/* const onCallAnswered = async (call) => {
  callStatus.value   = 'Connected';
  isCallActive.value = true;
  startTimer();

  if (remoteAudio.value && call.mediaStream) {
    remoteAudio.value.srcObject = call.mediaStream;
    remoteAudio.value.play().catch(() => {});
  }
  if (localAudio.value && call.localMediaStream) {
    localAudio.value.srcObject = call.localMediaStream;
  }
  // Set devices
  if (call.setAudioInDevice) {
    await call.setAudioInDevice(selectedInputDevice.value);
  }
  if (remoteAudio.value && remoteAudio.value.setSinkId) {
    await remoteAudio.value.setSinkId(selectedOutputDevice.value);
  }
}; */
  // Lifecycle hooks
  onMounted(async () => {
    // Check for pending call reviews
    try {
      const reviewResp = await getAnalyzedCalls();
      if (reviewResp.data && reviewResp.data.length > 0) {
        showReviewModal.value = true;
      }
    } catch (e) {
      console.error('Failed to check call reviews:', e);
    }

    window.addEventListener('click', () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume();
  }, { once: true });
  
  try {
    console.log('[DEBUG] Fetching Telnyx token...');
    logCallDebug('SYSTEM', 'Dialer initialization started').catch(console.error);
    
    const token = await store.dispatch('dialer/fetchToken');
    console.log('[DEBUG] Token fetched successfully');
    logCallDebug('SYSTEM', `Token fetched: ${token ? 'YES' : 'NO'}`).catch(console.error);
    
    console.log('[DEBUG] Creating TelnyxRTC client...');
    client = new TelnyxRTC({
      login_token: token,
      connection: '2688723432173995322',
      ringtoneFile: callingSound,   // incoming
      ringbackFile: callingSound,   // outgoing
      audio: {
        localElement:  localAudio.value,
        remoteElement: remoteAudio.value, 
        micId:         selectedInputDevice.value,
        speakerId:     selectedOutputDevice.value,
      },
      logLevel: 'trace'
    });
    
    console.log('[DEBUG] TelnyxRTC client created');
    logCallDebug('SYSTEM', 'TelnyxRTC client created successfully').catch(console.error);

    // READY EVENT
    client.on('telnyx.ready', () => {
      console.log('[DEBUG] 🟢 telnyx.ready fired');
      isReady.value = true;
      showToast('Dialer ready', 'info');
      logCallDebug('SYSTEM', 'Telnyx client ready').catch(console.error);
    });

    // ERROR EVENT
    client.on('telnyx.error', err => {
      console.error('[DEBUG] 🔴 telnyx.error:', err);
      //showToast('Telnyx error occurred', 'error');
      logCallDebug('SYSTEM', {
        event: 'telnyx.error',
        error: err,
        errorMessage: err?.message,
        errorName: err?.name
      }).catch(console.error);
    });

    // NOTIFICATION EVENT - Main event handler
    client.on('telnyx.notification', notification => {
  const call = notification.call;
  const event = notification.payload?.event_type || notification.type;
  
  console.log('[DEBUG] 📬 telnyx.notification received:', {
    event,
    callState: call?.state,
    callDirection: call?.direction,
    callId: call?.id
  });
  
  // Log every notification to the debug API
  const callNumber = call?.options?.destinationNumber || call?.options?.callerNumber || 'UNKNOWN';
  logCallDebug(callNumber, {
    event: 'telnyx.notification',
    type: event,
    callState: call?.state,
    callDirection: call?.direction,
    callId: call?.id,
    notification: {
      type: notification.type,
      caller_id_number: notification.caller_id_number,
      from: notification.from,
      payload: notification.payload
    }
  }).catch(console.error);

  if (event === 'callUpdate') {

    // RINGING STATE
    if (call.state === 'ringing') {
      console.log('[DEBUG] 📞 Call state: RINGING', {
        direction: call.direction,
        callId: call.id
      });
      
      logCallDebug(callNumber, {
        event: 'call_ringing',
        direction: call.direction,
        callId: call.id,
        options: call.options
      }).catch(console.error);

      if (call.direction === 'outbound') {
        console.log('[DEBUG] Outbound call ringing...');
        //call.answer();
      } else {
        console.log('[DEBUG] Incoming call detected');
        incomingCallerNumber.value = notification.caller_id_number || notification.from;
        isIncomingCall.value = true;
        currentCall.value = call;
        callStatus.value = 'Ringing…';
        
        ringtone.value.play().catch(err => {
          console.warn('⚠️ [WARN] Ring failed:', err);
          logCallDebug(callNumber, `Ringtone play failed: ${err.message}`).catch(console.error);
        });
        
        logCallDebug(callNumber, {
          event: 'incoming_call_ringing',
          caller: incomingCallerNumber.value
        }).catch(console.error);
      }
    }

    // ACTIVE STATE (CONNECTED)
    if (call.state === 'active') {
      console.log('[DEBUG] 🟢 Call state: ACTIVE (Connected)', {
        hasRemoteStream: !!call.remoteStream,
        callId: call.id
      });
      
      logCallDebug(callNumber, {
        event: 'call_active',
        hasRemoteStream: !!call.remoteStream,
        hasLocalStream: !!call.localStream,
        callId: call.id
      }).catch(console.error);

      if (call.remoteStream) {
        console.log('[DEBUG] Attaching remote stream to audio element');
        remoteAudio.value.srcObject = call.remoteStream;
        remoteAudio.value.play().then(
          () => {
            console.log('✅ [DEBUG] remoteAudio.play() succeeded');
            logCallDebug(callNumber, 'Remote audio playback started successfully').catch(console.error);
          },
          err => {
            console.warn('⚠️ [WARN] remoteAudio.play() failed:', err);
            logCallDebug(callNumber, {
              event: 'remote_audio_play_failed',
              error: err.message,
              name: err.name
            }).catch(console.error);
          }
        );
      } else {
        console.warn('⚠️ [WARN] No remoteStream yet on active state');
        logCallDebug(callNumber, 'WARNING: Call active but no remoteStream available').catch(console.error);
      }

      // UI updates
      ringtone.value.pause();
      ringtone.value.currentTime = 0;

      callStatus.value   = 'Connected';
      isCallActive.value = true;
      currentCall.value  = call;
      console.log('📶 [DEBUG] callStatus, isCallActive, currentCall updated');

      startTimer();
      
      logCallDebug(callNumber, {
        event: 'call_connected',
        callDuration: callDuration.value
      }).catch(console.error);
    }

    // HANGUP/CLEANUP STATES
    if (['destroy','ORIGINATOR_CANCEL','Request Terminated'].includes(call.state || call.cause || call.sipReason)) {
      console.log('[DEBUG] 🔴 Call ending/cleanup state detected:', {
        state: call.state,
        cause: call.cause,
        sipReason: call.sipReason
      });
      
      logCallDebug(callNumber, {
        event: 'call_ending',
        state: call.state,
        cause: call.cause,
        sipReason: call.sipReason,
        duration: callDuration.value
      }).catch(console.error);
      
      isIncomingCall.value = false;
      endCall();
    }
  } else {
    console.log('🤷 [DEBUG] Ignored event:', event);
    logCallDebug(callNumber, {
      event: 'ignored_event',
      eventType: event,
      notification
    }).catch(console.error);
  }
});

    // Additional event listeners for debugging
    console.log('[DEBUG] Setting up additional event listeners...');
    
    client.on('telnyx.socket.open', () => {
      console.log('[DEBUG] 🔌 telnyx.socket.open');
      logCallDebug('SYSTEM', 'WebSocket connection opened').catch(console.error);
    });
    
    client.on('telnyx.socket.close', (event) => {
      console.log('[DEBUG] 🔌 telnyx.socket.close:', event);
      logCallDebug('SYSTEM', {
        event: 'websocket_closed',
        code: event?.code,
        reason: event?.reason
      }).catch(console.error);
    });
    
    client.on('telnyx.socket.error', (error) => {
      console.log('[DEBUG] 🔌 telnyx.socket.error:', error);
      logCallDebug('SYSTEM', {
        event: 'websocket_error',
        error: String(error)
      }).catch(console.error);
    });

  console.log('[DEBUG] Connecting Telnyx client...');
  client.connect();
  logCallDebug('SYSTEM', 'Telnyx client.connect() called').catch(console.error);
  
    ['telnyx.call.failed','telnyx.call.hangup','telnyx.call.ringing','telnyx.call.answered']
      .forEach(evt => {
        client.on(evt, p => {
          console.log(`[DEBUG] 📡 Event: ${evt}`, p);
          logCallDebug(p?.destinationNumber || p?.callerNumber || 'UNKNOWN', {
            event: evt,
            payload: p
          }).catch(console.error);
        });
      });

  } catch (err) {
    console.error('[DEBUG] 🔴 Critical error during dialer initialization:', err);
    logCallDebug('SYSTEM', {
      event: 'initialization_failed',
      error: err,
      errorMessage: err?.message,
      errorName: err?.name
    }).catch(console.error);
    showToast('Failed to load dialer token', 'error');
  }

  // original device enumeration
  enumerateDevices();
  navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
  });



  onUnmounted(() => {
    stopTimer();
    window.removeEventListener('keydown', handleKeydown)

    // Clean up media stream
    if (mediaStream.value) {
      mediaStream.value.getTracks().forEach(track => track.stop());
    }
    
    // Clear toast timeout
    if (toast.value.timeout) {
      clearTimeout(toast.value.timeout);
    }
  
    // Remove device change listener
    navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
  });
  </script>
